import React from 'react';
import { useIntl } from 'react-intl';
import { toAbsoluteUrl } from '@helpers/index';
import { Recipe } from '../../../core/types';
import { RECIPE_INFO } from '../types';
import { isHiddenRecipe } from '@constants/subscription';
import { CheckIcon, WarningIcon } from '../icons';

export interface HealthPreviewRecipeSelectorProps {
  selectedRecipes: Recipe[];
  disabledRecipes: string[];
  petName?: string;
  sachets?: any[];
  onToggle: (recipe: Recipe) => void;
}

export const HealthPreviewRecipeSelector: React.FC<HealthPreviewRecipeSelectorProps> = ({
  selectedRecipes,
  disabledRecipes,
  petName,
  sachets,
  onToggle,
}) => {
  const intl = useIntl();

  const freshWith = (meatId: string) =>
    intl.formatMessage(
      { id: 'SUBSCRIPTION.FEEDING.FRESH_WITH' },
      { meat: intl.formatMessage({ id: meatId }) },
    );

  return (
    <div className="ppu-product-details">
      <div className="ppu-product-details__group">
        <span className="ppu-product-details__label">
          {intl.formatMessage({ id: 'PET_PROFILE.LABEL.RECIPE_AVAILABILITY' })}
        </span>
        <div className="ppu-recipe-list">
          {Object.values(RECIPE_INFO)
            .filter((info) => !isHiddenRecipe(info.id))
            .map((info) => {
              const recipe = info.id;
              const isSelected = selectedRecipes.includes(recipe);
              const isDisabled = disabledRecipes.includes(recipe);

              return (
                <div
                  key={recipe}
                  onClick={() => !isDisabled && onToggle(recipe)}
                  className={`ppu-recipe-item ${
                    isDisabled
                      ? 'ppu-recipe-item--disabled'
                      : isSelected
                        ? 'ppu-recipe-item--selected'
                        : 'ppu-recipe-item--selectable'
                  }`}>
                  <img
                    src={toAbsoluteUrl(info.icon)}
                    alt={info.name}
                    className="ppu-recipe-item__image"
                  />
                  <div className="ppu-recipe-item__info">
                    <span className="ppu-recipe-item__name">{freshWith(info.meatId)}</span>
                    <span className="ppu-recipe-item__protein">
                      {isDisabled
                        ? intl.formatMessage(
                            { id: 'PET_PROFILE.RECIPE.NOT_TOLERATED' },
                            {
                              name:
                                petName ||
                                intl.formatMessage({ id: 'PET_PROFILE.YOUR_DOG_FALLBACK' }),
                            },
                          )
                        : intl.formatMessage({ id: 'PET_PROFILE.RECIPE.AVAILABLE' })}
                    </span>
                  </div>
                  {isDisabled && (
                    <span className="ppu-recipe-item__warning">
                      <WarningIcon />
                    </span>
                  )}
                  {isSelected && !isDisabled && (
                    <div className="ppu-recipe-item__check">
                      <CheckIcon />
                    </div>
                  )}
                </div>
              );
            })}
        </div>
      </div>
      {sachets && sachets.length > 0 && (
        <div className="ppu-product-details__group">
          <span className="ppu-product-details__label">
            {intl.formatMessage({ id: 'PET_PROFILE.LABEL.YOUR_DELIVERY' })}
          </span>
          <ul className="ppu-sachets-list">
            {sachets
              .reduce(
                (
                  acc: {
                    type: string;
                    serve: number;
                    pouches: { quantity: number; value: number; unit: string }[];
                  }[],
                  sachet: any,
                ) => {
                  // Group two pouch slots of the same recipe (e.g. GS as
                  // 800g + 600g) into one row so days show once.
                  if (!sachet.quantity) return acc;
                  const pouch = {
                    quantity: sachet.quantity,
                    value: sachet.weight.value,
                    unit: sachet.weight.unit,
                  };
                  const existing = acc.find((group) => group.type === sachet.type);
                  if (existing) {
                    existing.pouches.push(pouch);
                  } else {
                    acc.push({ type: sachet.type, serve: sachet.serve, pouches: [pouch] });
                  }
                  return acc;
                },
                [],
              )
              .map((group) => {
                const sachetTypeText = RECIPE_INFO[group.type as Recipe]?.protein || group.type;
                const quantityText = group.pouches
                  .map((pouch) => `${pouch.quantity} x ${pouch.value}${pouch.unit}`)
                  .join(', ');
                return (
                  <li className="ppu-sachets-list__item" key={group.type}>
                    <span className="ppu-sachets-list__quantity">{quantityText}</span>
                    <span className="ppu-sachets-list__name">{sachetTypeText}</span>
                    <span className="ppu-sachets-list__serve">
                      {intl.formatMessage(
                        { id: 'PET_PROFILE.SACHETS.LASTS_DAYS' },
                        { days: group.serve },
                      )}
                    </span>
                  </li>
                );
              })}
          </ul>
        </div>
      )}
    </div>
  );
};
